import { useState } from "react";
import { Minus, Plus, Lock, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { IMG, http, quoteFor, usd, formatErr } from "@/lib/api";
import { SplitPay } from "@/components/site/SplitPay";

export const CheckoutSheet = ({ open, onOpenChange, stock }) => {
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const max = Math.max(1, Math.min(5, typeof stock === "number" ? stock : 5));
  const q = quoteFor(qty);
  const soldOut = stock === 0;

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/payments/checkout", { quantity: qty, origin_url: window.location.origin });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="checkout-modal" className="flex w-full flex-col border-l border-[#E3D9CD] bg-[#FDFBF7] p-0 sm:max-w-md">
        <SheetHeader className="border-b border-[#E3D9CD] p-6 text-left">
          <SheetTitle className="font-display text-3xl font-light italic">Your order</SheetTitle>
          <SheetDescription className="text-sm text-[#6E6661]">Free Recipe Almanac included. Address & payment on the next secure step.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex gap-4">
            <img src={IMG.studio} alt="The Slowpour 01" className="h-24 w-20 rounded-xl object-cover" />
            <div className="flex-1">
              <p className="font-display text-xl">The Slowpour 01</p>
              <p className="text-xs text-[#6E6661]">Matte black · Self-feeding</p>
              <div className="mt-3 inline-flex items-center rounded-full border border-[#E3D9CD]">
                <button data-testid="checkout-qty-decrease" onClick={() => setQty((v) => Math.max(1, v - 1))} className="p-2.5" aria-label="Decrease"><Minus size={14} /></button>
                <span data-testid="checkout-qty-value" className="w-8 text-center text-sm font-semibold">{qty}</span>
                <button data-testid="checkout-qty-increase" onClick={() => setQty((v) => Math.min(max, v + 1))} className="p-2.5" aria-label="Increase"><Plus size={14} /></button>
              </div>
            </div>
            <p className="font-semibold">{usd(q.subtotal)}</p>
          </div>
          <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[#F2EAE1] p-4 text-sm">
            <BookOpen size={18} className="text-[#C85A32]" />
            <span className="flex-1">The Recipe Almanac (PDF)</span>
            <span className="text-[#5E7153]">Free</span>
          </div>
          <dl className="mt-8 space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-[#6E6661]">Subtotal</dt><dd data-testid="checkout-summary-subtotal">{usd(q.subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#6E6661]">Shipping (flat rate)</dt><dd data-testid="checkout-summary-shipping">{usd(q.shipping)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#6E6661]">Sales tax (8.85%)</dt><dd data-testid="checkout-summary-tax">{usd(q.tax)}</dd></div>
            <div className="flex justify-between border-t border-[#E3D9CD] pt-4 text-lg font-semibold"><dt>Total</dt><dd data-testid="checkout-summary-total">{usd(q.total)}</dd></div>
          </dl>
          <div className="mt-5 rounded-2xl border border-[#E3D9CD] p-4"><SplitPay total={q.total} testId="checkout-split-pay-note" /></div>
        </div>
        <div className="border-t border-[#E3D9CD] p-6">
          <button
            data-testid="pay-button"
            disabled={loading || soldOut}
            onClick={pay}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#C85A32] py-4 font-semibold text-[#FDFBF7] transition-[background-color] duration-300 hover:bg-[#A84422] disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
            {soldOut ? "Sold out" : loading ? "Opening secure checkout…" : `Secure checkout · ${usd(q.total)}`}
          </button>
          <p className="mt-3 text-center text-xs text-[#6E6661]">Powered by Stripe · Card, Affirm, Klarna & Afterpay accepted.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
